import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, MapPin, ShieldCheck, Bike, Footprints, Clock, Banknote, CreditCard, Package, Star, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";
import { MedicineEntry } from "@/components/MedicineEntry";
import { AddressPicker } from "@/components/AddressPicker";

const TEAL = "#005C66";
const STEPS = ["Medicines", "Address", "Pharmacy"];

export default function OrderMedicines() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const userId = user?._id || user?.id || user?.userId;

  const [step, setStep] = useState(0);
  const [selection, setSelection] = useState({ items: [], hasRx: false, selectedRx: null });
  const [address, setAddress] = useState(null);
  const [match, setMatch] = useState(null);
  const [matching, setMatching] = useState(false);
  const [selectedPharmacy, setSelectedPharmacy] = useState(null);
  const [deliveryMode, setDeliveryMode] = useState("delivery");
  const [preorderMode, setPreorderMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onMedChange = useCallback((v) => setSelection(v), []);

  const itemCount = selection.items.length;
  const canContinueMeds = itemCount > 0 && (!selection.hasRx || selection.selectedRx);

  const goToAddress = () => {
    setError("");
    if (!itemCount) { setError("Add at least one medicine."); return; }
    if (selection.hasRx && !selection.selectedRx) { setError("A prescription is required for Schedule-H medicines."); return; }
    setStep(1);
  };

  const runMatch = useCallback(async (addr) => {
    setMatching(true);
    setError("");
    try {
      const res = await api.post(`/patient/pharmacies/match`, {
        lat: addr.lat, lng: addr.lng,
        items: selection.items.map((i) => ({ name: i.name })),
        prescription_id: selection.selectedRx || null,
      });
      setMatch(res.data);
    } catch {
      setError("Could not check pharmacy stock. Please try again.");
    } finally { setMatching(false); }
  }, [selection]);

  const onAddressSelected = (addr) => {
    setAddress(addr);
    setStep(2);
    runMatch(addr);
  };

  const groups = useMemo(() => {
    if (!match) return [];
    return [
      { key: "preferred", label: "Preferred (from your prescription)", items: match.preferred || [], Icon: Star },
      { key: "all", label: "All medicines available", items: match.allAvailable || [], Icon: ShieldCheck },
      { key: "partial", label: "Partially available", items: match.partial || [], Icon: Package },
    ].filter((g) => g.items.length);
  }, [match]);

  const placeOrder = async (isPreorder, pharmacyOverride) => {
    const pharm = pharmacyOverride || selectedPharmacy;
    if (!pharm) { setError("Select a pharmacy."); return; }
    setSubmitting(true);
    setError("");
    try {
      const fullAddr = [address.exactAddress, address.formattedAddress].filter(Boolean).join(", ");
      const res = await api.post(`/patient/${userId}/medicine-orders`, {
        pharmacy_id: pharm.id,
        pharmacy_name: pharm.name,
        items: selection.items,
        has_rx_items: selection.hasRx,
        prescription_document_id: selection.hasRx ? selection.selectedRx : null,
        delivery_mode: deliveryMode,
        delivery_address: deliveryMode === "delivery" ? fullAddr : null,
        delivery_address_id: address.addressId || null,
        delivery_lat: address.lat, delivery_lng: address.lng,
        is_preorder: isPreorder,
        payment_method: "cash",
      });
      if (res.data?.success) navigate("/medications?tab=orders");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not place order. Please try again.");
    } finally { setSubmitting(false); }
  };

  const renderPharmacy = (p) => {
    const selected = selectedPharmacy?.id === p.id;
    return (
      <Card key={p.id} data-testid={`match-pharmacy-${p.id}`} onClick={() => { setSelectedPharmacy(p); setPreorderMode(false); }} className={`rounded-2xl p-4 cursor-pointer ${selected ? "ring-2" : ""}`} style={selected ? { ["--tw-ring-color"]: TEAL } : {}}>
        <div className="flex items-center gap-2">
          <p className="font-semibold flex-1 truncate">{p.name}</p>
          {p.isPreferred && <span className="text-[10px] font-bold text-white px-2 py-0.5 rounded" style={{ backgroundColor: TEAL }}>Preferred</span>}
          {selected && <Check className="h-5 w-5" style={{ color: TEAL }} />}
        </div>
        {p.address && <p className="text-xs text-muted-foreground truncate mt-0.5">{p.address}</p>}
        <div className="flex items-center flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
          {p.distanceKm != null && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {p.distanceKm} km</span>}
          {p.is24x7 && <span className="flex items-center gap-1 text-green-600"><Clock className="h-3.5 w-3.5" /> 24x7</span>}
          {p.homeDelivery && <span className="flex items-center gap-1 text-blue-600"><Bike className="h-3.5 w-3.5" /> Delivery ≤ {p.deliveryRadiusKm}km</span>}
        </div>
        <div className="mt-2">
          {p.availability === "all"
            ? <span className="text-xs font-semibold text-emerald-700">All {p.totalCount} medicines in stock{p.estimatedTotal ? ` · ~₹${p.estimatedTotal}` : ""}</span>
            : <span className="text-xs font-semibold text-amber-700">{p.availableCount} of {p.totalCount} available</span>}
          <div className="flex flex-wrap gap-1 mt-1">
            {p.perItem.map((it, idx) => (
              <span key={idx} className={`text-[10px] px-1.5 py-0.5 rounded ${it.inStock ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>{it.name}{it.inStock ? "" : " ✕"}</span>
            ))}
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="max-w-2xl mx-auto px-4 pb-28">
      <div className="flex items-center gap-2 py-4 sticky top-0 bg-background z-20">
        <button onClick={() => (step === 0 ? navigate(-1) : setStep(step - 1))} data-testid="order-med-back" className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-accent"><ArrowLeft className="h-5 w-5" /></button>
        <h1 className="text-xl font-semibold">Order Medicines</h1>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-4">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: i <= step ? TEAL : "#CBD5E1" }}>{i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}</span>
              <span className={`text-xs font-semibold ${i === step ? "" : "text-muted-foreground"}`}>{s}</span>
            </div>
            {i < STEPS.length - 1 && <span className="flex-1 h-px" style={{ backgroundColor: i < step ? TEAL : "#E2E8F0" }} />}
          </div>
        ))}
      </div>

      {/* Step 1: Medicines */}
      {step === 0 && (
        <>
          <MedicineEntry userId={userId} onChange={onMedChange} />
          {error && <p className="text-red-600 text-sm mt-3" data-testid="order-med-error">{error}</p>}
          <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t">
            <div className="max-w-2xl mx-auto">
              <Button data-testid="order-med-continue" onClick={goToAddress} disabled={!canContinueMeds} className="w-full rounded-xl py-6 text-base" style={{ backgroundColor: TEAL }}>
                Continue ({itemCount} medicine{itemCount === 1 ? "" : "s"})
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Step 2: Address */}
      {step === 1 && (
        <>
          <p className="text-sm text-muted-foreground mb-3">Where should we deliver?</p>
          <AddressPicker userId={userId} onSelect={onAddressSelected} />
        </>
      )}

      {/* Step 3: Pharmacy match */}
      {step === 2 && (
        <>
          <Card className="rounded-2xl p-3 mb-4 flex items-start gap-2" style={{ backgroundColor: "#F0F9FA" }}>
            <MapPin className="h-4 w-4 mt-0.5" style={{ color: TEAL }} />
            <p className="text-sm flex-1">{address?.exactAddress ? `${address.exactAddress}, ` : ""}{address?.formattedAddress}</p>
            <button onClick={() => setStep(1)} className="text-xs font-semibold" style={{ color: TEAL }} data-testid="order-med-change-address">Change</button>
          </Card>

          {matching ? (
            <div className="py-10 text-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> Checking pharmacy stock near you…</div>
          ) : !match ? (
            <Card className="rounded-2xl p-6 text-center text-muted-foreground">{error || "No results."}</Card>
          ) : (
            <div className="space-y-5">
              {groups.map((g) => (
                <section key={g.key} data-testid={`match-group-${g.key}`}>
                  <h2 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide mb-2" style={{ color: TEAL }}>
                    <g.Icon className="h-4 w-4" /> {g.label}
                  </h2>
                  <div className="space-y-2">{g.items.map((p) => renderPharmacy(p))}</div>
                </section>
              ))}

              {groups.length === 0 && (
                <Card className="rounded-2xl p-5 text-center" data-testid="match-none">
                  <p className="text-sm text-muted-foreground mb-1">None of the nearby pharmacies have all your medicines in stock right now.</p>
                </Card>
              )}

              {/* Pre-order option */}
              {match.canPreorder && (match.preorderCandidates || []).length > 0 && (
                <Card className="rounded-2xl p-4 border-dashed" data-testid="preorder-section" style={{ borderColor: TEAL }}>
                  <p className="font-semibold text-sm">Not urgent? Place a pre-order</p>
                  <p className="text-xs text-muted-foreground mb-3">We&apos;ll reserve it with a pharmacy and deliver once stock arrives.</p>
                  {(match.preorderCandidates || []).map((c) => (
                    <button key={c.id} data-testid={`preorder-candidate-${c.id}`} onClick={() => { setPreorderMode(true); setSelectedPharmacy(c); }} className={`flex items-center gap-2 w-full py-2 text-left ${preorderMode && selectedPharmacy?.id === c.id ? "font-semibold" : ""}`}>
                      <span className="h-4 w-4 rounded-full border-2 flex items-center justify-center" style={{ borderColor: TEAL }}>{preorderMode && selectedPharmacy?.id === c.id && <span className="h-2 w-2 rounded-full" style={{ backgroundColor: TEAL }} />}</span>
                      <span className="text-sm flex-1 truncate">{c.name}</span>
                      <span className="text-xs text-muted-foreground">{c.distanceKm} km</span>
                    </button>
                  ))}
                </Card>
              )}

              {/* Order summary + fulfilment + payment */}
              {selectedPharmacy && (
                <Card className="rounded-2xl p-4" data-testid="order-summary">
                  <h2 className="font-bold mb-2">Order summary</h2>
                  <div className="space-y-1 mb-3">
                    {selection.items.map((it, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span className="truncate">{it.name}</span>
                        <span className="text-muted-foreground">{it.quantity || "1"}</span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between text-sm font-semibold pt-1 border-t mt-1">
                      <span>{selection.items.length} item{selection.items.length === 1 ? "" : "s"}</span>
                      <span>{selectedPharmacy.name}</span>
                    </div>
                  </div>

                  <p className="text-sm font-semibold mb-1">Fulfilment</p>
                  <div className="flex gap-2 mb-3">
                    <button data-testid="order-med-delivery" onClick={() => setDeliveryMode("delivery")} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm ${deliveryMode === "delivery" ? "text-white" : ""}`} style={deliveryMode === "delivery" ? { backgroundColor: TEAL, borderColor: TEAL } : { borderColor: "#D1E7E9" }}>
                      <Bike className="h-4 w-4" /> Delivery
                    </button>
                    <button data-testid="order-med-pickup" onClick={() => setDeliveryMode("pickup")} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm ${deliveryMode === "pickup" ? "text-white" : ""}`} style={deliveryMode === "pickup" ? { backgroundColor: TEAL, borderColor: TEAL } : { borderColor: "#D1E7E9" }}>
                      <Footprints className="h-4 w-4" /> Pickup
                    </button>
                  </div>

                  <p className="text-sm font-semibold mb-1">Payment</p>
                  <div className="flex items-center gap-3 py-2.5 px-4 rounded-xl border mb-1" style={{ borderColor: TEAL, backgroundColor: "#F0F9FA" }} data-testid="order-med-pay-cash">
                    <Banknote className="h-5 w-5" style={{ color: TEAL }} /> <span className="font-semibold text-sm">Cash</span>
                    <Check className="h-5 w-5 ml-auto" style={{ color: TEAL }} />
                  </div>
                  <div className="flex items-center gap-3 py-2.5 px-4 rounded-xl border opacity-70" style={{ borderColor: "#E2E8F0" }} data-testid="order-med-pay-online">
                    <CreditCard className="h-5 w-5 text-muted-foreground" /> <span className="font-semibold text-sm text-muted-foreground">Pay online</span>
                    <span className="ml-auto text-xs font-bold text-muted-foreground bg-muted px-2 py-1 rounded-lg">Coming soon</span>
                  </div>
                </Card>
              )}

              {error && <p className="text-red-600 text-sm" data-testid="order-med-error">{error}</p>}
            </div>
          )}

          {selectedPharmacy && (
            <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t">
              <div className="max-w-2xl mx-auto">
                <Button data-testid="order-med-place" onClick={() => placeOrder(preorderMode)} disabled={submitting} className="w-full rounded-xl py-6 text-base" style={{ backgroundColor: TEAL }}>
                  {submitting ? "Placing…" : preorderMode ? "Place pre-order" : "Place order"}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
